/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  SelectEditorDescription_Type,
  type InputData,
  type InputDescription,
} from "@smartsuite/automation-engine-api";
import {
  EmptyButton,
  Icon,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RoundButton,
  roundButtonStylesMedium,
} from "@smartsuite/react-ui/lib";
import { SolutionColors } from "@smartsuite/types";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as R from "remeda";
import type { AuthAttempt } from "../../../auth";
import { authDispatcher } from "../../../auth";
import { useEngineClient } from "../../../services/engineClient";
import { useRootStore } from "../../../stores/hooks";
import type { ActionCandidate } from "../../../types/candidate";
import { requiredInputData } from "../../../utils/form-validations";
import { SanitizedHTML } from "../../../utils/sanitizedHtml";
import * as AppStyle from "../../App/style";
import { SelectEditor } from "../AuthUnit/SelectEditor";
import { TextEditor } from "../AuthUnit/TextEditor";
import { emptyString } from "../AuthUnit/empty-string";
import * as S from "./style";
import {
  stringToValueDataV,
  toValueDataV,
} from "@smartsuite/automation-conditions";
import { translateIfPresent } from "../../../locales/i18n";

export const DynamicInputsForm = observer<{
  authAttempt: AuthAttempt;
  setAuthAttempt: (auth?: AuthAttempt) => void;
  integrationTitle?: string;
}>(
  ({
    authAttempt,
    setAuthAttempt,
    integrationTitle,
    // updateActionDescription,
  }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [inputs, setInputs] = useState<InputDescription[]>([]);
    const store = useRootStore();
    const engineClient = useEngineClient();

    const actionGroupType =
      store.automations.candidate.currentActionGroup?.config.oneofKind ??
      "actions";

    const makeDefaultValues = (): Record<string, InputData> => {
      if (authAttempt.authMethod.type.oneofKind === "dynamicInputs") {
        return Object.fromEntries(
          authAttempt.authMethod.type.dynamicInputs.inputs.map((input) => [
            input.inputId,
            {
              inputId: input.inputId,
              value: {
                values: [
                  {
                    value: {
                      oneofKind: "string",
                      string:
                        authAttempt.initialValues !== undefined
                          ? authAttempt.initialValues[input.inputId]
                          : "",
                    },
                    label: "",
                    icon: "",
                  },
                ],
                metadata: {},
              },
            },
          ]),
        );
      } else {
        return {};
      }
    };

    const { control, handleSubmit, formState, register, getValues, setValue } =
      useForm<Record<string, InputData>>({
        defaultValues: makeDefaultValues(),
      });

    useEffect(() => {
      if (authAttempt.authMethod.type.oneofKind !== "dynamicInputs") {
        return;
      }
      setTitle(authAttempt.authMethod.type.dynamicInputs.title);
    }, [authAttempt]);

    useEffect(() => {
      if (authAttempt.authMethod.type.oneofKind !== "dynamicInputs") {
        return;
      }

      setInputs(authAttempt.authMethod.type.dynamicInputs.inputs);
      setDescription(authAttempt.authMethod.type.dynamicInputs.description);
    }, [authAttempt]);

    const onSubmit = (data: Record<string, InputData>): void => {
      setLoading(true);
      void authDispatcher(
        authAttempt,
        Object.values(data),
        store,
        engineClient,
        actionGroupType,
      )
        .then(() => {
          setAuthAttempt();
        })
        .catch(() => {
          setErrorMessage(
            t("automation-form.invalid-integration-credentials", {
              label: integrationTitle,
            }),
          );
        })
        .finally(() => {
          setLoading(false);
        });
    };

    const handleDynamicInputChange = (): void => {
      setLoading(true);
      const data = getValues();
      const action: ActionCandidate | undefined = R.find(
        store.automations.candidate.allActions,
        (a) => a.hash === authAttempt.target.hash,
      );
      if (action === undefined) {
        return;
      }

      const inputData: InputData[] = inputs
        .map((input) => {
          return data[input.inputId];
        })
        .filter((input) => !!input);

      void engineClient
        .getCredentialDynamicInputsDescription({
          actionId: action.actionReference?.actionId ?? "",
          inputs: inputData,
          integrationId: authAttempt.integrationId,
        })
        .then((result) => {
          setInputs(result.inputs);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    return (
      <ModalOverlay animation="fade" active={true} closeByEsc={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AppStyle.LocalCssVariablesWrapper>
            <S.Container>
              <Modal
                styles={{ width: 500 }}
                onClose={() => setAuthAttempt()}
                size="SM"
                withoutHat={true}
                header={
                  <>
                    <ModalHeader
                      title={translateIfPresent(`caption.${title}`, title)}
                      titleSize="xxs"
                    />
                    <S.Close>
                      <Icon
                        name="close"
                        color="currentColor"
                        onClick={() => setAuthAttempt()}
                        transition
                      />
                    </S.Close>
                  </>
                }
                footer={
                  <ModalFooter
                    actions={
                      R.isTruthy(errorMessage) && (
                        <S.FooterError>
                          <Icon
                            name={"info-baloon"}
                            size={"M"}
                            color={SolutionColors.basePrimaryRed}
                          />
                          <S.FooterErrorText>{errorMessage}</S.FooterErrorText>
                        </S.FooterError>
                      )
                    }
                    buttons={
                      <>
                        <div style={{ marginRight: "10px" }}>
                          <EmptyButton
                            size="lg"
                            onClick={() => setAuthAttempt()}
                          >
                            {t("general.cancel")}
                          </EmptyButton>
                        </div>
                        <RoundButton
                          type="submit"
                          styles={roundButtonStylesMedium}
                          loading={loading}
                          onClick={() => {}}
                        >
                          {t("general.connect")}
                        </RoundButton>
                      </>
                    }
                  ></ModalFooter>
                }
              >
                <S.ModalContent>
                  <S.DescriptionList>
                    <S.Desciription>
                      <SanitizedHTML
                        html={translateIfPresent(
                          `caption.${description}`,
                          description,
                        )}
                        options={{
                          allowedTags: ["br", "span", "p", "b", "a"],
                          allowedAttributes: {
                            a: ["href", "target"],
                            span: ["style"],
                          },
                        }}
                      />
                    </S.Desciription>
                    <S.InputList>
                      {inputs.map((description, i) => {
                        if (description.editor?.editor.oneofKind === "text") {
                          return (
                            <Controller
                              key={description.inputId}
                              name={description.inputId}
                              rules={{
                                validate: description.editor.required
                                  ? { required: requiredInputData }
                                  : {},
                              }}
                              control={control}
                              defaultValue={emptyString(description.inputId)}
                              render={({
                                field: { onChange, onBlur, value },
                              }) => (
                                <>
                                  <TextEditor
                                    key={`${description.inputId}_editor`}
                                    description={description}
                                    input={value}
                                    hasError={R.isTruthy(
                                      formState.errors[description.inputId]
                                        ?.message,
                                    )}
                                    onChange={(nextInput) => {
                                      onChange(nextInput);
                                      onBlur();
                                      if (description.dynamic) {
                                        handleDynamicInputChange();
                                      }
                                    }}
                                  />
                                  {formState.errors[description.inputId]
                                    ?.message && (
                                    <S.ErrorMessage>
                                      {
                                        formState.errors[description.inputId]
                                          ?.message
                                      }
                                    </S.ErrorMessage>
                                  )}
                                </>
                              )}
                            ></Controller>
                          );
                        }
                        if (description.editor?.editor.oneofKind === "select") {
                          return (
                            <Controller
                              key={description.inputId}
                              name={description.inputId}
                              rules={{
                                validate: description.editor.required
                                  ? { required: requiredInputData }
                                  : {},
                              }}
                              control={control}
                              render={({
                                field: { onChange, onBlur, value },
                                fieldState,
                                formState,
                              }) => {
                                let input = value;
                                const localDescription = structuredClone(
                                  toJS(description),
                                );
                                if (
                                  localDescription.editor?.editor.oneofKind ===
                                    "select" &&
                                  localDescription.editor?.editor.select
                                    .type ===
                                    SelectEditorDescription_Type.DROPDOWN_WITH_DETAILS
                                ) {
                                  localDescription.editor.editor.select.options =
                                    localDescription.editor?.editor.select.options.map(
                                      (option: any) => {
                                        const value = JSON.parse(option.value);
                                        if (value.slug !== undefined) {
                                          return {
                                            ...option,
                                            value: value.slug,
                                          };
                                        }
                                        return value;
                                      },
                                    );
                                }
                                if (
                                  JSON.stringify(value) ===
                                    JSON.stringify(
                                      makeDefaultValues()[description.inputId],
                                    ) &&
                                  !fieldState.isTouched
                                ) {
                                  if (description.dynamic) {
                                    handleDynamicInputChange();
                                    onBlur();
                                  }
                                }
                                if (input === undefined) {
                                  input = emptyString(description.inputId);
                                }
                                return (
                                  <>
                                    <SelectEditor
                                      key={`${description.inputId}_editor`}
                                      description={localDescription}
                                      input={input}
                                      hasError={R.isTruthy(
                                        formState.errors[description.inputId]
                                          ?.message,
                                      )}
                                      onChange={(nextInput) => {
                                        onChange(nextInput);
                                        onBlur();
                                        if (description.dynamic) {
                                          handleDynamicInputChange();
                                        }
                                      }}
                                    />
                                    {formState.errors[description.inputId]
                                      ?.message && (
                                      <S.ErrorMessage>
                                        {
                                          formState.errors[description.inputId]
                                            ?.message
                                        }
                                      </S.ErrorMessage>
                                    )}
                                  </>
                                );
                              }}
                            ></Controller>
                          );
                        }
                        return <Fragment key={description.inputId} />;
                      })}
                    </S.InputList>
                  </S.DescriptionList>
                </S.ModalContent>
              </Modal>
            </S.Container>
          </AppStyle.LocalCssVariablesWrapper>
        </form>
      </ModalOverlay>
    );
  },
);
